import React, { useState, useEffect } from "react";
import { DefaultPageLayout } from "../../../../ui/layouts/DefaultPageLayout";
import { Avatar } from "../../../../ui/components/Avatar";
import { Button } from "../../../../ui/components/Button";
import { IconWithBackground } from "../../../../ui/components/IconWithBackground";
import { IconButton } from "../../../../ui/components/IconButton";
import { Table } from "../../../../ui/components/Table";
import { TextField } from "../../../../ui/components/TextField";
import { Tabs } from "../../../../ui/components/Tabs";
import { Loader } from "../../../../ui/components/Loader";
import { StatusBadgeForInvoicesAndBills } from "../../../../ui/components/StatusBadgeForInvoicesAndBills";
import { SortDropdown } from "../../../../shared/presentation/components/SortDropdown";
import TableSkeletonRows from "../../../../shared/presentation/components/TableSkeletonRows";
import { FeatherArrowLeft } from "@subframe/core";
import { FeatherCalendar } from "@subframe/core";
import { FeatherCheckCircle } from "@subframe/core";
import { FeatherChevronLeft } from "@subframe/core";
import { FeatherChevronRight } from "@subframe/core";
import { FeatherEye } from "@subframe/core";
import { FeatherRefreshCw } from "@subframe/core";
import { FeatherSearch } from "@subframe/core";
import { FeatherXCircle } from "@subframe/core";
import { useNavigate } from "react-router-dom";
import {
  getPostedStatusLabel,
  getStoredAccountingBackend,
} from "../../../../config/accounting";
import { InvoiceEntity } from "../../domain/entities/InvoiceEntity";
import { useAllInvoices, TabType } from "../hooks/useAllInvoices";

const AllInvoicesPage: React.FC = () => {
  const postedLabel = getPostedStatusLabel(getStoredAccountingBackend());
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState<TabType>("posted");
  const [itemsPerPage] = useState(10);

  const { documents, loading, error, stats, pagination, fetchInvoicesByTab } =
    useAllInvoices();

  // Sort state for the current tab
  const [currentSort, setCurrentSort] = useState<{
    by: "created_at" | "updated_at";
    order: "asc" | "desc";
    label: string;
  }>({
    by: "created_at",
    order: "desc",
    label: "Created Date: Newest First",
  });

  // Helper function to format date
  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString;
      }

      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  // Helper function to get document amount
  const getDocumentAmount = (document: InvoiceEntity): string => {
    if ("invoice_json" in document && document.invoice_json) {
      return `${
        document.invoice_json.currency_symbol
      }${document.invoice_json.total.toLocaleString("en-IN")}`;
    }
    return "N/A";
  };

  // Helper function to get document name/customer
  const getDocumentName = (document: InvoiceEntity): string => {
    if ("invoice_json" in document && document.invoice_json) {
      return document.invoice_json.customer_name || "Unknown Customer";
    }
    return "Unknown";
  };

  // Helper function to get document number
  const getDocumentNumber = (document: InvoiceEntity): string => {
    if ("invoice_json" in document && document.invoice_json) {
      return document.invoice_json.invoice_number || document.id.toString();
    }
    return document.id.toString();
  };

  // Helper function to get invoice date
  const getInvoiceDate = (document: InvoiceEntity): string => {
    if ("invoice_json" in document && document.invoice_json) {
      return (
        document.invoice_json.date ||
        document.invoice_json.invoice_date ||
        "-"
      );
    }
    return "-";
  };

  // Helper function to get due date
  const getDueDate = (document: InvoiceEntity): string => {
    if ("invoice_json" in document && document.invoice_json) {
      return document.invoice_json.due_date || "-";
    }
    return "-";
  };

  // Navigate to review page
  const handleReviewClick = (documentId: string, document: InvoiceEntity) => {
    navigate(`/invoices/review`, {
      state: {
        invoiceJson: document.invoice_json,
        fileName: document.original_filename,
        documentId: documentId,
        invoiceDocument: document,
        signedUrl: document.signed_url,
        isCustomerMatched:
          document.invoice_json.customer_id !== undefined &&
          document.invoice_json.customer_id !== "",
        isIdMatched: document.invoice_json.invoice_number !== "",
      },
    });
  };

  // Filter documents based on search query (client-side filtering on current page)
  const getFilteredDocuments = (): InvoiceEntity[] => {
    if (!searchQuery.trim()) {
      return documents;
    }

    const lowerSearchTerm = searchQuery.toLowerCase();
    return documents.filter((doc) => {
      const invoiceNumber = getDocumentNumber(doc).toLowerCase();
      const customerName = getDocumentName(doc).toLowerCase();
      const amount = getDocumentAmount(doc).toLowerCase();

      return (
        invoiceNumber.includes(lowerSearchTerm) ||
        customerName.includes(lowerSearchTerm) ||
        amount.includes(lowerSearchTerm)
      );
    });
  };

  // Pagination handlers
  const handleNextPage = () => {
    if (pagination?.has_more) {
      fetchInvoicesByTab(
        activeTab,
        (pagination?.current_page || 1) + 1,
        itemsPerPage,
        currentSort.by,
        currentSort.order
      );
    }
  };

  const handlePreviousPage = () => {
    if ((pagination?.current_page || 1) > 1) {
      fetchInvoicesByTab(
        activeTab,
        (pagination?.current_page || 1) - 1,
        itemsPerPage,
        currentSort.by,
        currentSort.order
      );
    }
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= (pagination?.total_pages || 0)) {
      fetchInvoicesByTab(
        activeTab,
        page,
        itemsPerPage,
        currentSort.by,
        currentSort.order
      );
    }
  };

  // Handle tab change
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    // Reset sort to default for new tab
    const defaultSort: {
      by: "created_at" | "updated_at";
      order: "asc" | "desc";
      label: string;
    } = {
      by: "created_at",
      order: "desc",
      label: "Created Date: Newest First",
    };
    setCurrentSort(defaultSort);
    // Fetch data for the new tab
    fetchInvoicesByTab(tab, 1, itemsPerPage, defaultSort.by, defaultSort.order);
  };

  // Fetch initial data when component mounts
  useEffect(() => {
    fetchInvoicesByTab(
      activeTab,
      1,
      itemsPerPage,
      currentSort.by,
      currentSort.order
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <DefaultPageLayout>
      <div className="flex h-full w-full flex-col items-start">
        <div className="flex w-full flex-col items-start gap-4 border-b border-solid border-neutral-border bg-default-background px-8 py-6">
          <Button
            variant="brand-secondary"
            icon={<FeatherArrowLeft />}
            onClick={() => navigate("/dashboard/invoices")}
          >
            Back to Invoices
          </Button>
          <div className="flex w-full flex-wrap items-start justify-between">
            <div className="flex flex-col items-start gap-1">
              <span className="text-heading-2 font-heading-2 text-default-font">
                All Invoices
              </span>
              <span className="text-body font-body text-subtext-color">
                Manage and track all invoice processing
              </span>
            </div>
          </div>
          <div className="flex w-full flex-wrap items-start gap-4">
            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-2 rounded-md border border-solid border-neutral-border px-6 py-6">
              <div className="flex items-center gap-2">
                <IconWithBackground
                  variant="success"
                  icon={<FeatherCheckCircle />}
                />
                <span className="text-body-bold font-body-bold text-subtext-color">
                  {postedLabel}
                </span>
              </div>
              <span className="text-heading-2 font-heading-2 text-success-600">
                {loading ? <Loader size="small" /> : stats.posted}
              </span>
            </div>
            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-2 rounded-md border border-solid border-neutral-border px-6 py-6">
              <div className="flex items-center gap-2">
                <IconWithBackground
                  variant="neutral"
                  icon={<FeatherCalendar />}
                />
                <span className="text-body-bold font-body-bold text-subtext-color">
                  Pending
                </span>
              </div>
              <span className="text-heading-2 font-heading-2 text-default-font">
                {loading ? <Loader size="small" /> : stats.pending}
              </span>
            </div>
            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-2 rounded-md border border-solid border-neutral-border px-6 py-6">
              <div className="flex items-center gap-2">
                <IconWithBackground variant="error" icon={<FeatherXCircle />} />
                <span className="text-body-bold font-body-bold text-subtext-color">
                  Rejected
                </span>
              </div>
              <span className="text-heading-2 font-heading-2 text-error-600">
                {loading ? <Loader size="small" /> : stats.rejected}
              </span>
            </div>
            <div className="flex grow shrink-0 basis-0 flex-col items-start gap-2 rounded-md border border-solid border-neutral-border px-6 py-6">
              <div className="flex items-center gap-2">
                <IconWithBackground variant="error" icon={<FeatherXCircle />} />
                <span className="text-body-bold font-body-bold text-subtext-color">
                  Duplicate
                </span>
              </div>
              <span className="text-heading-2 font-heading-2 text-error-600">
                {loading ? <Loader size="small" /> : stats.duplicate}
              </span>
            </div>
          </div>
          <div className="flex w-full items-center justify-between">
            <TextField
              className="h-auto w-96 flex-none"
              variant="filled"
              label=""
              helpText=""
              icon={<FeatherSearch />}
            >
              <TextField.Input
                placeholder="Search invoices by number, customer, amount..."
                value={searchQuery}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setSearchQuery(event.target.value)
                }
              />
            </TextField>
            <div className="flex items-center gap-2">
              <SortDropdown
                currentSort={currentSort}
                onSortChange={(
                  sortBy: string,
                  sortOrder: string,
                  label: string
                ) => {
                  setCurrentSort({
                    by: sortBy as "created_at" | "updated_at",
                    order: sortOrder as "asc" | "desc",
                    label,
                  });
                  fetchInvoicesByTab(
                    activeTab,
                    1,
                    itemsPerPage,
                    sortBy as "created_at" | "updated_at",
                    sortOrder as "asc" | "desc"
                  );
                }}
                disabled={loading}
              />
              <Button
                variant="neutral-tertiary"
                icon={<FeatherRefreshCw />}
                onClick={() => {
                  fetchInvoicesByTab(
                    activeTab,
                    pagination?.current_page || 1,
                    itemsPerPage,
                    currentSort.by,
                    currentSort.order
                  );
                }}
                disabled={loading}
              >
                Refresh
              </Button>
            </div>
          </div>
          <Tabs>
            <Tabs.Item
              active={activeTab === "all"}
              onClick={() => handleTabChange("all")}
            >
              All
            </Tabs.Item>
            <Tabs.Item
              active={activeTab === "posted"}
              onClick={() => handleTabChange("posted")}
            >
              {postedLabel}
            </Tabs.Item>
            <Tabs.Item
              active={activeTab === "pending"}
              onClick={() => handleTabChange("pending")}
            >
              Pending
            </Tabs.Item>
            <Tabs.Item
              active={activeTab === "rejected"}
              onClick={() => handleTabChange("rejected")}
            >
              Rejected
            </Tabs.Item>
            <Tabs.Item
              active={activeTab === "duplicate"}
              onClick={() => handleTabChange("duplicate")}
            >
              Duplicate
            </Tabs.Item>
          </Tabs>
        </div>
        <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-6 bg-default-background px-8 py-6 overflow-auto">
          <Table
            header={
              <Table.HeaderRow>
                <Table.HeaderCell>File Name</Table.HeaderCell>
                <Table.HeaderCell>Invoice #</Table.HeaderCell>
                <Table.HeaderCell>Customer</Table.HeaderCell>
                <Table.HeaderCell>Amount</Table.HeaderCell>
                <Table.HeaderCell>Invoice Date</Table.HeaderCell>
                <Table.HeaderCell>Due Date</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
                <Table.HeaderCell />
              </Table.HeaderRow>
            }
          >
            {loading ? (
              <TableSkeletonRows rowCount={itemsPerPage} columnCount={8} />
            ) : error ? (
              <Table.Row>
                <Table.Cell colSpan={8}>
                  <div className="flex w-full items-center justify-center py-8">
                    <span className="text-body font-body text-error-600">
                      {error}
                    </span>
                  </div>
                </Table.Cell>
              </Table.Row>
            ) : getFilteredDocuments().length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={8}>
                  <div className="flex w-full items-center justify-center py-8">
                    <span className="text-body font-body text-neutral-500">
                      {searchQuery.trim()
                        ? "No invoices match your search"
                        : "No invoices found"}
                    </span>
                  </div>
                </Table.Cell>
              </Table.Row>
            ) : (
              getFilteredDocuments().map((document) => (
                <Table.Row key={document.id}>
                  <Table.Cell>
                    <span className="whitespace-nowrap text-body font-body text-default-font">
                      {document.original_filename || "N/A"}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
                      {getDocumentNumber(document)}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex items-center gap-2">
                      <Avatar size="small" square={true}>
                        {getDocumentName(document).charAt(0).toUpperCase()}
                      </Avatar>
                      <span className="whitespace-nowrap text-body font-body text-default-font">
                        {getDocumentName(document)}
                      </span>
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="whitespace-nowrap text-body-bold font-body-bold text-default-font">
                      {getDocumentAmount(document)}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="whitespace-nowrap text-body font-body text-neutral-500">
                      {formatDate(getInvoiceDate(document))}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <span className="whitespace-nowrap text-body font-body text-neutral-500">
                      {formatDate(getDueDate(document))}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <StatusBadgeForInvoicesAndBills
                      status={
                        "status" in document ? document.status : "unknown"
                      }
                      reviewed={
                        "reviewed" in document ? document.reviewed : "0"
                      }
                      type="invoice"
                    />
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex items-center gap-1">
                      <IconButton
                        icon={<FeatherEye />}
                        onClick={() =>
                          handleReviewClick(document.id.toString(), document)
                        }
                      />
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))
            )}
          </Table>

          {/* Pagination Controls */}
          {(pagination?.total_pages || 0) > 1 && !loading && (
            <div className="flex w-full items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-body font-body text-subtext-color">
                  Showing{" "}
                  {((pagination?.current_page || 1) - 1) * itemsPerPage + 1} to{" "}
                  {Math.min(
                    (pagination?.current_page || 1) * itemsPerPage,
                    pagination?.total || 0
                  )}{" "}
                  of {pagination?.total || 0} results
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="neutral-tertiary"
                  size="small"
                  onClick={handlePreviousPage}
                  disabled={(pagination?.current_page || 1) === 1}
                  icon={<FeatherChevronLeft />}
                >
                  Previous
                </Button>

                {/* Page numbers */}
                <div className="flex items-center gap-1">
                  {Array.from(
                    { length: Math.min(5, pagination?.total_pages || 0) },
                    (_, i) => {
                      let pageNum: number;
                      if ((pagination?.total_pages || 0) <= 5) {
                        pageNum = i + 1;
                      } else if ((pagination?.current_page || 1) <= 3) {
                        pageNum = i + 1;
                      } else if (
                        (pagination?.current_page || 1) >=
                        (pagination?.total_pages || 0) - 2
                      ) {
                        pageNum = (pagination?.total_pages || 0) - 4 + i;
                      } else {
                        pageNum = (pagination?.current_page || 1) - 2 + i;
                      }

                      return (
                        <Button
                          key={pageNum}
                          variant={
                            (pagination?.current_page || 1) === pageNum
                              ? "brand-primary"
                              : "neutral-tertiary"
                          }
                          size="small"
                          onClick={() => handlePageChange(pageNum)}
                          className="min-w-[32px]"
                        >
                          {pageNum}
                        </Button>
                      );
                    }
                  )}
                </div>

                <Button
                  variant="neutral-tertiary"
                  size="small"
                  onClick={handleNextPage}
                  disabled={!pagination?.has_more}
                  icon={<FeatherChevronRight />}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </DefaultPageLayout>
  );
};

export default AllInvoicesPage;
